/**
 * HelpDot — el «?» de la ayuda contextual.
 *
 * Abre el tema de la Academia en un cajón lateral, sin salir de la ficha ni
 * perder lo que el usuario estaba escribiendo.
 *
 *   <HelpDot topic="racks.form" />
 *
 * Si el ancla no existe, no pinta nada (y el test de cobertura lo delata).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuestionMarkCircleIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { resolveHelpAnchor } from './helpAnchors';
import BlockRenderer from './blocks';

export default function HelpDot({ topic, label = 'Ayuda de esta pantalla', className = '' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const entry = resolveHelpAnchor(topic);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!entry) return null;

  const goToAcademy = () => {
    setOpen(false);
    navigate(`/academia/${entry.chapterId}/${entry.id}`);
  };

  return (
    <>
      <button
        type="button"
        className={`ac-helpdot ${className}`.trim()}
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
      >
        <QuestionMarkCircleIcon className="ac-helpdot-icon" />
      </button>

      {open && (
        <div className="ac-help-overlay" role="presentation" onClick={() => setOpen(false)}>
          <aside
            className="ac-help-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={entry.title}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="ac-help-head">
              <div>
                <span className="ac-help-eyebrow">{entry.chapterTitle}</span>
                <h2 className="ac-help-title">{entry.title}</h2>
              </div>
              <button
                type="button"
                className="ac-help-close"
                onClick={() => setOpen(false)}
                aria-label="Cerrar ayuda"
              >
                <XMarkIcon className="ac-help-close-icon" />
              </button>
            </header>
            <div className="ac-help-body">
              {entry.summary && <p className="ac-help-summary">{entry.summary}</p>}
              <BlockRenderer blocks={entry.blocks} />
            </div>
            <footer className="ac-help-foot">
              <button type="button" className="ac-help-more" onClick={goToAcademy}>
                Ver en la Academia
                <ArrowRightIcon className="ac-help-more-icon" />
              </button>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}
